import React from "react";
import { useTranslation } from "react-i18next";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getTribes, joinTribe, leaveTribe } from "@/data/requests/tribe-requests";
import { CreateTribeDialog } from "@/components/tribes/create-tribe-dialog";
import { Users, LogIn, LogOut, MessageSquare, UserPlus } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import AuthStore from "../../../stores/AuthStore";
import { useNavigate } from "react-router-dom";
import { CardGridLoader } from "@/components/shared/page-loader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserSearch } from "@/components/users/user-search";

export function TribesScreen() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = AuthStore.user;

  const { data: tribesResponse, isLoading } = useQuery({
    queryKey: ["tribes"],
    queryFn: getTribes,
  });

  const tribesData = tribesResponse?.data?.data || tribesResponse?.data || [];
  const tribes = Array.isArray(tribesData) ? tribesData : [];

  const joinMutation = useMutation({
    mutationFn: joinTribe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tribes"] });
      toast({ title: t("tribes.joinedSuccess") });
    },
  });

  const leaveMutation = useMutation({
    mutationFn: leaveTribe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tribes"] });
      toast({ title: t("tribes.leftSuccess") });
    },
  });

  const handleJoin = (id) => joinMutation.mutate(id);
  const handleLeave = (id) => leaveMutation.mutate(id);
  const handleEnter = (id) => navigate(`/tribes/${id}`);

  return (
    <AppLayout 
      title={t("tribes.title")} 
      description={t("tribes.subtitle")}
      actions={[
        {
          name: t("tribes.createTribe"),
          icon: <Users className="h-4 w-4" />,
          onClick: () => document.getElementById("create-tribe-trigger")?.click(),
        }
      ]}
    >
      <div className="hidden">
        <CreateTribeDialog>
          <button id="create-tribe-trigger">Trigger</button>
        </CreateTribeDialog>
      </div>

      <Tabs defaultValue="tribes" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-8 max-w-[400px]">
          <TabsTrigger value="tribes">{t("tribes.tabs.tribes")}</TabsTrigger>
          <TabsTrigger value="people">{t("tribes.tabs.users")}</TabsTrigger>
        </TabsList>

        <TabsContent value="tribes">
          {isLoading ? (
            <CardGridLoader />
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {tribes.map((tribe) => {
                const isMember = tribe.members.some(m => m.user.id === user?.id);
                
                return (
                  <Card 
                    key={tribe.id} 
                    className="flex flex-col cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => handleEnter(tribe.id)}
                  >
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-xl">{tribe.name}</CardTitle>
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {tribe._count?.members || tribe.members?.length || 0}
                        </Badge>
                      </div>
                      <CardDescription className="line-clamp-2 mt-2">
                        {tribe.description || t("tribes.noDescription")}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1">
                      <div className="flex -space-x-2 overflow-hidden">
                        {(tribe.members || []).slice(0, 5).map((member) => (
                          <div 
                            key={member.user?.id || member.id}
                            className="inline-block h-8 w-8 rounded-full ring-2 ring-background bg-primary/10 flex items-center justify-center text-xs font-medium"
                            title={member.user?.name || member.name}
                          >
                            {(member.user?.name || member.name || 'U')?.charAt(0).toUpperCase()}
                          </div>
                        ))}
                        {(tribe._count?.members || tribe.members?.length || 0) > 5 && (
                          <div className="inline-block h-8 w-8 rounded-full ring-2 ring-background bg-muted flex items-center justify-center text-xs font-medium">
                            +{(tribe._count?.members || tribe.members?.length || 0) - 5}
                          </div>
                        )}
                      </div>
                    </CardContent>
                    <CardFooter className="pt-4 border-t" onClick={(e) => e.stopPropagation()}>
                      {isMember ? (
                        <div className="flex gap-2 w-full">
                          <Button className="flex-1" onClick={() => handleEnter(tribe.id)}>
                            <MessageSquare className="mr-2 h-4 w-4" /> {t("tribes.open")}
                          </Button>
                          <Button variant="outline" size="icon" onClick={(e) => {
                            e.stopPropagation();
                            handleLeave(tribe.id);
                          }}>
                            <LogOut className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button className="w-full" variant="secondary" onClick={(e) => {
                          e.stopPropagation();
                          handleJoin(tribe.id);
                        }}>
                          <LogIn className="mr-2 h-4 w-4" /> {t("tribes.joinTribe")}
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="people">
          <UserSearch />
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}
